'use client';

import {
  ButtonV3,
  InverseHeader,
  LayoutGrid,
  ModalV3,
  PlayFilledIcon,
  TypographyV3,
} from '@jouzen/ecom-components';
import { useTranslations } from 'next-intl';
import React, { useState } from 'react';

import ArtDirectionImage from '@/app/components/Image/ArtDirectionImage';
import Video from '@/app/components/Video';
import useDisplayBestBuy from '@/app/hooks/useDisplayBestBuy';
import useFeatureFlag from '@/app/hooks/useFeatureFlag';
import useMatchUserCountry from '@/app/hooks/useMatchUserCountry';

import InStoreAvailable from './_components/InStoreAvailable';
import VisitBestBuy from './_components/VisitBestBuy';
import { sizingTips } from './_data/sizingTips';

const Content = (): JSX.Element => {
  const [videoOpen, setVideoOpen] = useState(false);
  const handleCloseModal = () => setVideoOpen(false);
  const isBestBuyLinkActive = useFeatureFlag('best-buy-store-locator-page');
  const isBestBuyCountry = useDisplayBestBuy();
  const isJapan = useMatchUserCountry('JP');
  const inStoreAvailable = isJapan || isBestBuyCountry;
  const t = useTranslations();

  const captions = [
    {
      label: 'English',
      lang: 'en',
      src: '/captions/captions-en.vtt',
    },
  ];

  const handlePlayVideo = () => {
    setVideoOpen(true);
  };

  return (
    <>
      <section data-cy="sizing-hero-section">
        <InverseHeader enter className="relative pb-48 pt-28 lg:py-36">
          <div className="absolute left-0 top-0 size-full">
            <ArtDirectionImage
              fill
              sizes="100vw"
              alt="sizing_female_hand_alt"
              className="object-cover"
              format="png"
              srcSet={[
                {
                  src: 'sizing/woman_hand_mobile',
                  media: '(max-width: 767px)',
                },
                {
                  src: 'sizing/woman_hand_desktop',
                  media: '(min-width: 768px)',
                },
              ]}
            />
          </div>
          <LayoutGrid className="relative">
            <div className="col-main lg:col-end-14">
              <TypographyV3
                className="mb-4 uppercase tracking-wide"
                color="light"
                Element="h1"
                variant="body-large"
                weight="bold"
              >
                {t('footer_sizing')}
              </TypographyV3>
              <TypographyV3 variant="h2" color="light" height="tight">
                {t.rich('sizing_title')}
              </TypographyV3>
              <button
                aria-label={t('watch_the_video')}
                onClick={handlePlayVideo}
                className="mt-8 flex items-center gap-x-1"
                data-cy="watch-the-video-button"
              >
                <div className="flex items-center justify-center rounded-full bg-blue-100 p-2 text-sandstone-200 hover:bg-blue-200">
                  <PlayFilledIcon width={20} height={20} />
                </div>
                <TypographyV3
                  className="cursor-pointer pl-4"
                  variant="body-large"
                  color="light"
                >
                  {t('watch_the_video')}
                </TypographyV3>
              </button>
            </div>
          </LayoutGrid>
        </InverseHeader>
      </section>
      <section className="relative -mt-7 rounded-t-xl bg-sandstone-100 pb-20 pt-14 md:rounded-t-[1.75rem] md:pt-20 xl:py-24">
        <LayoutGrid>
          <div className="col-main lg:col-end-13">
            <TypographyV3
              variant="h4"
              Element="h2"
              height="normal"
              className="mb-6"
            >
              {t('sizing_tips')}
            </TypographyV3>
          </div>
          <div className="col-main lg:col-start-13">
            {sizingTips.map(({ id, title, text }) => (
              <div key={id} className="mb-6">
                <TypographyV3 variant="h4" className="mb-4">
                  {`${id}. ${t(title)}`}
                </TypographyV3>
                <TypographyV3 variant="body-large">{t(text)}</TypographyV3>
              </div>
            ))}
          </div>
        </LayoutGrid>
        <LayoutGrid className="mt-24">
          <div className="col-main lg:col-end-13">
            <TypographyV3
              variant="h4"
              Element="h2"
              height="none"
              className="mb-6"
            >
              {t('sizing_confirm_size')}
            </TypographyV3>
          </div>
          <div className="col-main lg:col-start-13">
            <TypographyV3 variant="body-large" className="max-w-lg">
              {t('sizing_my_account_link')}
            </TypographyV3>
            <ButtonV3
              variant="secondary-dark"
              className="mt-12"
              href="/my-account"
            >
              {t('confirm_your_size')}
            </ButtonV3>
          </div>
        </LayoutGrid>

        {isBestBuyLinkActive && isBestBuyCountry && <VisitBestBuy />}
        {inStoreAvailable && (
          <InStoreAvailable
            isBestBuyCountry={isBestBuyCountry}
            isBestBuyLinkActive={isBestBuyLinkActive}
            isJapan={isJapan}
          />
        )}
      </section>
      <ModalV3
        isOpen={videoOpen}
        onOpenChange={handleCloseModal}
        padding="large"
        size="large"
      >
        <div
          className="absolute left-0 top-1/2 -translate-y-1/2 px-6 md:relative md:top-0 md:translate-y-0 xl:px-24"
          data-cy="sizing-video-wrapper"
        >
          <Video
            src="sizing/sizing_video.mp4"
            type="video/mp4"
            className="size-full"
            contentTitle="sizing_video"
            location="sizing-hero"
            playVideo={videoOpen}
            captions={captions}
            altImage={{
              src: 'sizing/alt_img_for_video',
              fill: true,
              alt: 'sizing_video_alt',
              format: 'png',
              sizes: '100vw',
            }}
          />
        </div>
      </ModalV3>
    </>
  );
};

export default Content;
